import type { Rng } from '../rng';
import { luckOf, secondChanceFrame } from './luck';
import type { ActResult, CasinoGame, TableEnv, TableOption } from './types';

/**
 * Liar's Dice against two AI players (decided with the user 2026-09-23):
 * three dice each, 1s wild. Everyone antes the bet; the last player with
 * dice takes the pot, less the house rake.
 *
 * Each round everyone rolls in secret. Players take turns raising the bid
 * ("four 5s" = at least four dice on the table show a 5 or a wild 1) or
 * calling "Liar!" on the last one. On a call, the dice are revealed: if the
 * bid was short the bidder loses a die, otherwise the caller does. The
 * round's loser opens the next round. You're locked in until the game ends.
 *
 * AI moves play out on the real-time tick (AI_MS apart) so the lens shows
 * the bidding as it happens. The pane shows the odds of the highlighted
 * bid from your own dice, as a learning aid.
 */
export const DICE_EACH = 3;
const AI_MS = 900;
/** Winner takes the pot (everyone's ante) less this share. Set from the
 *  sim (2026-09-23): against Rosa and Vic the best simple strategy (call
 *  under 30%, raise to the highest bid at least 70% likely, no bluffing)
 *  wins ~36.7% of games, so 10% leaves it ~99%; a middling style wins ~30%
 *  (~82%). A player who also reads the other bids can do better — it's a
 *  skill game. Balance patch (2026-09-23): played through the lens (raises
 *  listed only up to RAISE_COUNTS above the bid) the same strategy returned
 *  ~95.8% at 10%, so the rake is 7% (~99%). */
export const RAKE = 0.07;
/** The ★'s luck: how often you keep a die you'd have lost, by luck level.
 *  Calibrated by sim (the best simple strategy) to 103% / 110%; pinned in
 *  luck.test.ts. */
export const SECOND_CHANCE = [0, 0.037, 0.075];
/** Your turn offers every raise up to this many counts above the current
 *  bid (the list scrolls; 8 was once used as a pane-size cap, which hid
 *  ordinary bids like 2 x 5s from an opening player). */
const RAISE_COUNTS = 3;

export interface Personality {
  name: string;
  /** Calls "Liar!" when the bid looks less likely than this. */
  callAt: number;
  /** Raises to the highest bid at least this likely... */
  target: number;
  /** ...give or take up to this much bluff. */
  bluff: number;
}

/** Rosa is careful; Vic bluffs more and calls less. Targets are high
 *  because they jump to the highest bid they believe (see decide). */
export const OPPONENTS: readonly Personality[] = [
  { name: 'Rosa', callAt: 0.4, target: 0.72, bluff: 0.1 },
  { name: 'Vic', callAt: 0.3, target: 0.62, bluff: 0.25 },
];
const NAMES = ['You', ...OPPONENTS.map((o) => o.name)];

export interface Bid {
  count: number;
  face: number;
  by: number;
}

export interface LiarsDiceState {
  phase: 'idle' | 'bidding' | 'reveal' | 'done';
  /** Each player's current roll (empty once out). Index 0 is you. */
  dice: number[][];
  bid: Bid | null;
  turn: number;
  /** Recent bids and calls, newest last. */
  log: string[];
  /** When the next AI move happens (ms). */
  nextAt: number;
  /** The last call: the bid, how many there really were, who lost a die. */
  reveal: { caller: number; bid: Bid; actual: number; loser: number; saved?: boolean } | null;
  bet: number;
  returned: number;
  /** You lost a die this game (the jackpot needs a clean sweep). */
  lostDie: boolean;
}

/** Dice showing `face`, counting wild 1s. */
export function matches(dice: readonly number[], face: number): number {
  return dice.filter((d) => d === face || d === 1).length;
}

function choose(n: number, k: number): number {
  let r = 1;
  for (let i = 0; i < k; i++) r = (r * (n - i)) / (i + 1);
  return r;
}

/** Chance at least `need` of `n` unseen dice show a face (1 in 3 with wilds). */
export function chanceAtLeast(need: number, n: number): number {
  if (need <= 0) return 1;
  let p = 0;
  for (let k = need; k <= n; k++) p += choose(n, k) * (1 / 3) ** k * (2 / 3) ** (n - k);
  return p;
}

/** How likely each of a bidder's own dice shows the face they bid. Players
 *  bid what they hold, so it's well above the 1 in 3 of an unseen die; 0.7
 *  made the "Liar!" hint match what actually happens (measured 2026-09-23
 *  against Rosa and Vic — counting only your own dice, a bid shown as 0-20%
 *  likely was really true 39% of the time). */
const BIDDER_MATCH = 0.7;

/** Chance of at least `need` matches among `other` unseen dice (1 in 3
 *  each) plus `bidder` dice (BIDDER_MATCH each). */
function chanceWithBidder(need: number, other: number, bidder: number): number {
  if (need <= 0) return 1;
  let p = 0;
  for (let a = 0; a <= other; a++) {
    const pa = choose(other, a) * (1 / 3) ** a * (2 / 3) ** (other - a);
    for (let b = Math.max(0, need - a); b <= bidder; b++) p += pa * choose(bidder, b) * BIDDER_MATCH ** b * (1 - BIDDER_MATCH) ** (bidder - b);
  }
  return p;
}

/** Chance a bid is true, from what `own` dice show and how many are hidden. */
export function bidChance(own: readonly number[], totalDice: number, bid: { count: number; face: number }): number {
  return chanceAtLeast(bid.count - matches(own, bid.face), totalDice - own.length);
}

/** The next higher bid: same count on a higher face, else one more 2. */
function nextBid(bid: { count: number; face: number } | null): { count: number; face: number } {
  if (!bid) return { count: 1, face: 2 };
  return bid.face < 6 ? { count: bid.count, face: bid.face + 1 } : { count: bid.count + 1, face: 2 };
}

/** Legal raises over `bid`, lowest first. */
export function raises(bid: Bid | null, totalDice: number, limit: number): { count: number; face: number }[] {
  const out: { count: number; face: number }[] = [];
  for (let b = nextBid(bid); b.count <= totalDice && out.length < limit; b = nextBid(b)) out.push(b);
  return out;
}

/** An AI (or the sim player) decides: call, or raise to a bid. */
export function decide(
  own: readonly number[],
  totalDice: number,
  bid: Bid | null,
  who: Personality,
  rng: Rng,
): 'liar' | { count: number; face: number } {
  if (bid && bidChance(own, totalDice, bid) < who.callAt) return 'liar';
  const options = raises(bid, totalDice, 30);
  if (!options.length) return 'liar';
  // Jump to the highest bid it believes (with some nerve), not the smallest
  // step — creeping up one face at a time made for dull, long rounds.
  const nerve = rng.next() * who.bluff;
  const believed = options.filter((b) => bidChance(own, totalDice, b) + nerve >= who.target);
  if (believed.length) return believed[believed.length - 1]!;
  return bid ? 'liar' : options[0]!;
}

const faceName = (face: number) => `${face}s`;
const bidText = (b: { count: number; face: number }) => `${b.count} x ${faceName(b.face)}`;
const totalDice = (s: LiarsDiceState) => s.dice.reduce((n, d) => n + d.length, 0);

function nextPlayer(s: LiarsDiceState, from: number): number {
  for (let step = 1; step <= s.dice.length; step++) {
    const i = (from + step) % s.dice.length;
    if (s.dice[i]!.length > 0) return i;
  }
  return from;
}

function roll(counts: readonly number[], rng: Rng): number[][] {
  return counts.map((n) => Array.from({ length: n }, () => rng.int(6) + 1).sort((a, b) => a - b));
}

function placeBid(s: LiarsDiceState, by: number, bid: { count: number; face: number }, now: number): LiarsDiceState {
  return {
    ...s,
    bid: { ...bid, by },
    log: [...s.log, `${NAMES[by]}: ${bidText(bid)}`].slice(-6),
    turn: nextPlayer(s, by),
    nextAt: now + AI_MS,
  };
}

/** A "Liar!" call: reveal, take a die, and settle the game if it's over. */
function call(s: LiarsDiceState, caller: number, env: TableEnv): ActResult<LiarsDiceState> {
  const bid = s.bid!;
  const actual = s.dice.reduce((n, d) => n + matches(d, bid.face), 0);
  const loser = actual >= bid.count ? caller : bid.by;
  // The ★'s luck: sometimes you keep a die you'd have lost.
  const saved = loser === 0 && env.rng.next() < (SECOND_CHANCE[luckOf(env)] ?? 0);
  // The cups lift one at a time (Rosa, Vic, then yours), then the count.
  const head = `${NAMES[caller]}: Liar! on ${bidText(bid)}`;
  const order = [1, 2, 0].filter((i) => s.dice[i]!.length > 0);
  const frames = order.map((_, k) => [head, ...order.slice(0, k + 1).map((i) => `${NAMES[i]}: ${s.dice[i]!.join(' ')}`)].join('\n'));
  frames.push(`${frames.at(-1)!}\n\n${faceName(bid.face)} (1s wild): ${actual}`);
  if (saved) frames.push(secondChanceFrame(frames.at(-1)!));
  const counts = s.dice.map((d, i) => d.length - (i === loser && !saved ? 1 : 0));
  const lostDie = s.lostDie || (loser === 0 && !saved);
  const next: LiarsDiceState = {
    ...s,
    phase: 'reveal',
    reveal: { caller, bid, actual, loser, saved },
    log: [...s.log, `${NAMES[caller]}: Liar!`].slice(-6),
    lostDie,
  };
  const youOut = counts[0] === 0;
  const youWon = counts.every((n, i) => i === 0 || n === 0);
  if (!youOut && !youWon) return { state: next, bankroll: env.bankroll, frames };
  const pot = s.bet * s.dice.length;
  const returned = youWon ? Math.floor(pot * (1 - RAKE)) : 0;
  return {
    state: { ...next, phase: 'done', returned },
    bankroll: env.bankroll + returned,
    settled: { wagered: s.bet, returned },
    jackpot: youWon && !lostDie,
    frames,
  };
}

/** Counts left after the last reveal (the loser's die comes off here). */
function countsAfter(s: LiarsDiceState): number[] {
  return s.dice.map((d, i) => d.length - (s.reveal && s.reveal.loser === i && !s.reveal.saved ? 1 : 0));
}

export const liarsDice: CasinoGame<LiarsDiceState> = {
  id: 'liarsdice',
  usesLuck: true,
  name: "Liar's Dice",
  blurb: 'Bid on hidden dice against two sharps. Last one standing takes the pot.',
  baseMinBet: 5,
  baseMaxBet: 100,
  specialPerk: 'Luck: sometimes you keep a die you would have lost.',
  jackpotRule: 'Win a game without losing a die.',
  lockedHint: '(finish the game)',
  create: () => ({ phase: 'idle', dice: [], bid: null, turn: 0, log: [], nextAt: 0, reveal: null, bet: 0, returned: 0, lostDie: false }),
  locked: (s) => s.phase === 'bidding' || s.phase === 'reveal',
  options(s): TableOption[] {
    if (s.phase === 'idle' || s.phase === 'done') return [{ id: 'play', label: s.phase === 'done' ? 'Play again' : 'Deal in' }];
    if (s.phase === 'reveal') return [{ id: 'next', label: 'Next round' }];
    if (s.turn !== 0) return [{ id: 'wait', label: `${NAMES[s.turn]} is thinking` }];
    const upTo = (s.bid?.count ?? 0) + RAISE_COUNTS;
    const opts: TableOption[] = raises(s.bid, totalDice(s), 100).filter((b) => b.count <= upTo).map((b) => ({ id: `bid:${b.count}:${b.face}`, label: `Bid ${bidText(b)}` }));
    if (s.bid) opts.push({ id: 'liar', label: 'Liar!' });
    return opts;
  },
  describe(s, env, highlighted) {
    if (s.phase === 'idle') {
      return 'Three dice each; 1s are wild. Raise the bid or call "Liar!" Wrong side of a call loses a die. Last one standing wins.';
    }
    // Every line stays within the pane's 25 characters so none is rewrapped.
    const others = OPPONENTS.map((o, i) => `${o.name}: ${s.dice[i + 1]!.length} dice`).join('  ');
    if (s.phase === 'reveal' || s.phase === 'done') {
      const r = s.reveal!;
      const lines = s.dice.map((d, i) => `${NAMES[i]}: ${d.length ? d.join(' ') : 'out'}`);
      lines.push(`${NAMES[r.caller]}: Liar! on ${bidText(r.bid)}`);
      lines.push(`There were ${r.actual} x ${faceName(r.bid.face)}.`);
      lines.push(r.loser === 0 ? (r.saved ? '★ You keep your die!' : 'You lose a die.') : `${NAMES[r.loser]} loses a die.`);
      if (s.phase === 'done') lines.push(s.returned > 0 ? `You win the pot! +$${s.returned - s.bet}` : `You're out. -$${s.bet}`);
      return lines.join('\n');
    }
    const lines = [`You: ${s.dice[0]!.join(' ')}`, others, ...s.log.slice(-3)];
    if (s.turn === 0 && highlighted) {
      const total = totalDice(s);
      if (highlighted === 'liar' && s.bid) {
        // Reads the bid too: the bidder likely holds some of that face.
        const bidder = s.dice[s.bid.by]!.length;
        const need = s.bid.count - matches(s.dice[0]!, s.bid.face);
        const pTrue = chanceWithBidder(need, total - s.dice[0]!.length - bidder, bidder);
        lines.push('', `Chance it's a lie: ${Math.round((1 - pTrue) * 100)}%`);
      } else if (highlighted.startsWith('bid:')) {
        const [, count, face] = highlighted.split(':').map(Number);
        lines.push('', `Chance it's true: ${Math.round(bidChance(s.dice[0]!, total, { count: count!, face: face! }) * 100)}%`);
      }
    }
    return lines.join('\n');
  },
  act(s, optionId, env) {
    if (optionId === 'play' && (s.phase === 'idle' || s.phase === 'done')) {
      const dice = roll(Array(NAMES.length).fill(DICE_EACH), env.rng);
      return {
        state: { ...s, phase: 'bidding', dice, bid: null, turn: 0, log: ['New game. You open.'], nextAt: env.now, reveal: null, bet: env.bet, returned: 0, lostDie: false },
        bankroll: env.bankroll - env.bet,
      };
    }
    if (optionId === 'next' && s.phase === 'reveal') {
      const counts = countsAfter(s);
      const dice = roll(counts, env.rng);
      const loser = s.reveal!.loser;
      const opener = counts[loser]! > 0 ? loser : nextPlayer({ ...s, dice }, loser);
      return {
        state: { ...s, phase: 'bidding', dice, bid: null, turn: opener, log: [`${NAMES[opener]} ${opener === 0 ? 'open' : 'opens'}.`], nextAt: env.now + AI_MS, reveal: null },
        bankroll: env.bankroll,
      };
    }
    if (s.phase !== 'bidding' || s.turn !== 0) return { state: s, bankroll: env.bankroll };
    if (optionId === 'liar' && s.bid) return call(s, 0, env);
    if (optionId.startsWith('bid:')) {
      const [, count, face] = optionId.split(':').map(Number);
      const legal = raises(s.bid, totalDice(s), 100).some((b) => b.count === count && b.face === face);
      if (legal) return { state: placeBid(s, 0, { count: count!, face: face! }, env.now), bankroll: env.bankroll };
    }
    return { state: s, bankroll: env.bankroll };
  },
  tick(s, env) {
    if (s.phase !== 'bidding' || s.turn === 0 || env.now < s.nextAt) return null;
    const who = OPPONENTS[s.turn - 1]!;
    const choice = decide(s.dice[s.turn]!, totalDice(s), s.bid, who, env.rng);
    if (choice === 'liar') return call(s, s.turn, env);
    return { state: placeBid(s, s.turn, choice, env.now), bankroll: env.bankroll };
  },
};

